import React, { FC, memo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from '@reach/router';
import { SubCard } from '../../../common/api';
import { BlockMarkupViewer } from '../../../markup/blockMarkupViewer/blockMarkupViewer';
import { markupFormatter, BlockMarkup, subcardKey } from '../../../common';
import { ThemeProps } from '../../../styles/theme';
import {
  SubcardContainer,
  SubcardCheckbox,
  SubcardContent,
  SubcardQuestion,
  SubcardAnswer
} from './multicard.styles';

export interface SubcardStat {
  correct: number;
  total: number;
}

// Green (accurate) -> red (inaccurate).
function accuracyColor(stat: SubcardStat): string {
  const hue = Math.round((stat.correct / stat.total) * 120);
  return `hsl(${hue}, 60%, 42%)`;
}

const GroupPill = styled.div<ThemeProps>`
  display: inline-block;
  align-self: flex-start;
  margin-bottom: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.theme.buttonText};
  background-color: ${props => props.theme.accentColor};
`;

const AccuracyBadge = styled.button`
  flex-shrink: 0;
  align-self: flex-start;
  min-width: 44px;
  min-height: 24px;
  padding: 3px 8px;
  border: none;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    opacity: 0.85;
  }
`;

/**
 * Props for SubcardDisplay component
 */
export interface SubcardDisplayProps {
  /** The subcard data to display */
  subcard: SubCard;
  /** Index of this subcard in the parent card */
  index: number;
  /** Whether this subcard is selected */
  isSelected: boolean;
  /** Whether this subcard is selectable */
  isSelectable: boolean;
  /** Whether to show the checkbox for selection */
  showCheckbox: boolean;
  /** Whether to show the index badge */
  showIndex: boolean;
  /** Whether to show the answer section */
  showAnswer: boolean;
  /** The card ID for markup rendering */
  cardId?: string;
  /** Per-subcard review stats; when present (and has attempts) shows an accuracy badge */
  stat?: SubcardStat;
  /** Callback when selection changes */
  onSelectionChange?: (index: number, isSelected: boolean) => void;
  /** Callback when subcard is clicked */
  onClick?: (index: number) => void;
}

/**
 * Component for displaying individual subcards with selection capabilities
 */
const SubcardDisplayComponent: FC<SubcardDisplayProps> = memo(({
  subcard,
  index,
  isSelected,
  isSelectable,
  showCheckbox,
  showIndex: _showIndex,
  showAnswer,
  cardId,
  stat,
  onSelectionChange,
  onClick
}) => {
  const navigate = useNavigate();

  const handleStatClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (cardId !== undefined && cardId.length > 0) {
      navigate(`/card/${encodeURIComponent(cardId)}/subcard/${subcardKey(subcard.question, subcard.answer)}`);
    }
  }, [navigate, cardId, subcard.question, subcard.answer]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (onSelectionChange) {
      onSelectionChange(index, event.target.checked);
    }
  }, [index, onSelectionChange]);

  // Handle container click
  const handleContainerClick = useCallback(() => {
    if (isSelectable && onClick) {
      onClick(index);
    }
  }, [index, isSelectable, onClick]);

  // Handle key press for accessibility
  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isSelectable && onClick) {
        onClick(index);
      }
    }
  }, [index, isSelectable, onClick]);

  const questionMarkup = React.useMemo((): BlockMarkup => {
    return markupFormatter.parseBlock(subcard.question);
  }, [subcard.question]);

  const answerMarkup = React.useMemo((): BlockMarkup => {
    return markupFormatter.parseBlock(subcard.answer);
  }, [subcard.answer]);

  return (
    <SubcardContainer
      $isSelected={isSelected}
      $isSelectable={isSelectable}
      onClick={handleContainerClick}
      onKeyPress={handleKeyPress}
      tabIndex={isSelectable ? 0 : -1}
      role={isSelectable ? 'button' : undefined}
      aria-pressed={isSelectable ? isSelected : undefined}
      aria-label={isSelectable ? `Select subcard ${index + 1}` : undefined}
    >
      {showCheckbox && (
        <SubcardCheckbox
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          disabled={!isSelectable}
          aria-label={`Select subcard ${index + 1}`}
        />
      )}
      
      
      <SubcardContent>
        {subcard.group !== undefined && subcard.group.length > 0 && (
          <GroupPill>{subcard.group}</GroupPill>
        )}
        <SubcardQuestion>
          {questionMarkup.length > 0 ? (
            <BlockMarkupViewer
              cardId={cardId}
              blockMarkup={questionMarkup}
            />
          ) : (
            subcard.question
          )}
        </SubcardQuestion>

        {showAnswer && (
          <SubcardAnswer>
            {answerMarkup.length > 0 ? (
              <BlockMarkupViewer
                cardId={cardId}
                blockMarkup={answerMarkup}
              />
            ) : (
              subcard.answer
            )}
          </SubcardAnswer>
        )}
      </SubcardContent>

      {stat !== undefined && stat.total > 0 && (
        <AccuracyBadge
          type="button"
          style={{ backgroundColor: accuracyColor(stat) }}
          title={`${stat.correct}/${stat.total} correct — tap for details`}
          onClick={handleStatClick}
        >
          {Math.round((stat.correct / stat.total) * 100)}%
        </AccuracyBadge>
      )}
    </SubcardContainer>
  );
});

SubcardDisplayComponent.displayName = 'SubcardDisplay';

export const SubcardDisplay = SubcardDisplayComponent;